import { useQuery } from '@tanstack/react-query';
import { useAuth } from './auth';
import { useHouseholdId, type Login } from './household';
import { supabase } from './supabase';
import { assertCompleteRows } from './aggregateState';

export type AiCall = {
  id: string; person_id: string | null; kind: string; model: string;
  input_tokens: number | null; output_tokens: number | null; ok: boolean; created_at: string;
};

/** Mounted only inside the admin UI gate. Existing household RLS still applies.
 * This does not establish a new database-level admin-only permission boundary.
 * Refuse capped results rather than presenting partial operational totals. */
export function useAdminRecords(since: string) {
  const hid = useHouseholdId(); const uid = useAuth().session?.user.id;
  const access = useQuery({
    queryKey: ['admin-access', hid, uid], enabled: !!hid && !!uid,
    queryFn: async ({ signal }) => {
      const result = await supabase.from('household_memberships')
        .select('id,person_id,invited_email,role,accepted_at', { count: 'exact' })
        .eq('household_id', hid).is('revoked_at', null).order('invited_email').range(0, 999).abortSignal(signal);
      if (result.error) throw result.error;
      assertCompleteRows(result, 'Access list');
      return result.data as Login[];
    },
  });
  const usage = useQuery({
    queryKey: ['admin-ai', hid, uid, since], enabled: !!hid && !!uid,
    queryFn: async ({ signal }) => {
      const result = await supabase.from('ai_calls')
        .select('id,person_id,kind,model,input_tokens,output_tokens,ok,created_at', { count: 'exact' })
        .eq('household_id', hid).gte('created_at', since).order('created_at', { ascending: false })
        .range(0, 999).abortSignal(signal);
      if (result.error) throw result.error;
      assertCompleteRows(result, 'AI activity');
      return result.data as AiCall[];
    },
  });
  return { access, usage };
}

export function tokenEstimate(rows: AiCall[], inputRate: string, outputRate: string) {
  const validRate = (s: string) => s.trim() !== '' && Number.isFinite(Number(s)) && Number(s) >= 0;
  const known = rows.filter(r => r.input_tokens !== null && r.output_tokens !== null && r.input_tokens >= 0 && r.output_tokens >= 0);
  return {
    excluded: rows.length - known.length,
    usd: validRate(inputRate) && validRate(outputRate)
      ? known.reduce((sum, r) => sum + (r.input_tokens! * Number(inputRate) + r.output_tokens! * Number(outputRate)) / 1_000_000, 0)
      : null,
  };
}
